import { createContext, useContext, useState, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

/* ── Context ─────────────────────────────────────────────── */

interface TabsContextValue {
    activeTab: string;
    setActiveTab: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
    const ctx = useContext(TabsContext);
    if (!ctx) throw new Error('Tabs compound components must be used within <Tabs.Root>');
    return ctx;
}

/* ── Root ─────────────────────────────────────────────────── */

export interface TabsRootProps {
    defaultValue: string;
    value?: string;
    onChange?: (value: string) => void;
    children: ReactNode;
    className?: string;
}

function Root({ defaultValue, value, onChange, children, className }: TabsRootProps) {
    const [internal, setInternal] = useState(defaultValue);
    const activeTab = value ?? internal;

    const setActiveTab = (next: string) => {
        if (value === undefined) setInternal(next);
        onChange?.(next);
    };

    return (
        <TabsContext.Provider value={{ activeTab, setActiveTab }}>
            <div className={className}>{children}</div>
        </TabsContext.Provider>
    );
}

/* ── List (container for Tab items) ──────────────────────── */

export interface TabsListProps {
    children: ReactNode;
    className?: string;
}

function List({ children, className }: TabsListProps) {
    return (
        <div role="tablist" className={twMerge('flex border-b border-gray-200', className)}>
            {children}
        </div>
    );
}

/* ── Tab (individual tab button) ─────────────────────────── */

export interface TabsTriggerProps {
    value: string;
    children: ReactNode;
    className?: string;
    disabled?: boolean;
}

function Trigger({ value, children, className, disabled }: TabsTriggerProps) {
    const { activeTab, setActiveTab } = useTabsContext();
    const isActive = activeTab === value;

    return (
        <button
            role="tab"
            type="button"
            aria-selected={isActive}
            disabled={disabled}
            onClick={() => setActiveTab(value)}
            className={twMerge(
                'px-4 py-2 font-poppins text-sm font-semibold transition-colors cursor-pointer',
                'border-b-2 -mb-px',
                isActive
                    ? 'text-primary-700 border-primary-700'
                    : 'text-gray-400 border-transparent hover:text-gray-600',
                disabled && 'opacity-50 cursor-not-allowed',
                className,
            )}
        >
            {children}
        </button>
    );
}

/* ── Panel (content for a tab) ───────────────────────────── */

export interface TabsPanelProps {
    value: string;
    children: ReactNode;
    className?: string;
}

function Panel({ value, children, className }: TabsPanelProps) {
    const { activeTab } = useTabsContext();
    if (activeTab !== value) return null;

    return (
        <div role="tabpanel" className={className}>
            {children}
        </div>
    );
}

/* ── Compound export ─────────────────────────────────────── */

export const Tabs = {
    Root,
    List,
    Trigger,
    Panel,
};
